import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import SharedNavigation from "@/components/SharedNavigation";
import { optimizeImage } from "@/lib/mediaUrls";

const formatFullDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-US", {
      month: "long", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return String(d); }
};

const ReportDetailPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);
      } catch { /* anon ok */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/reports/${reportId}`);
        setReport(r.data);
      } catch {
        setReport(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [reportId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <SharedNavigation currentUser={user} />
        <div className="pt-24 text-center text-gray-400">Loading…</div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-black">
        <SharedNavigation currentUser={user} />
        <div className="pt-24 max-w-2xl mx-auto px-4 text-center">
          <h1 className="text-2xl text-white mb-2">Report not found</h1>
          <Link to="/reports">
            <Button className="bg-purple-600 text-white">Back to RGM Reports</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="report-detail-page">
      <SharedNavigation currentUser={user} />
      <div className="pt-20 max-w-3xl mx-auto px-4 pb-12">
        <button
          onClick={() => navigate(-1)}
          className="text-purple-400 hover:text-purple-300 text-sm mb-4"
          data-testid="report-back-btn"
        >
          ← Back
        </button>
        <Card className="bg-gray-900/40 border-gray-800 overflow-hidden">
          {report.cover_image && (
            <div className="w-full aspect-video bg-black/50 overflow-hidden">
              <img src={optimizeImage(report.cover_image, 'cover')} alt={report.title} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="p-6">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border font-semibold bg-purple-500/20 text-purple-200 border-purple-500/40">
                {report.category}
              </span>
              {report.is_featured && (
                <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-500/30 text-amber-200 border border-amber-500/50 font-semibold">
                  ★ Featured
                </span>
              )}
              {report.is_auto && (
                <span className="text-[10px] text-gray-500">auto-generated</span>
              )}
              <span className="text-xs text-gray-500 ml-auto">{formatFullDate(report.published_at || report.created_at)}</span>
            </div>
            <h1 className="text-white text-3xl sm:text-4xl font-bold mb-3 leading-tight" data-testid="report-title">
              {report.title}
            </h1>
            {(report.source_user_name || report.source_crew_name) && (
              <div className="text-sm text-gray-400 mb-4">
                {report.source_user_id && report.source_user_name && (
                  <>About{" "}
                    <Link to={`/artist/${report.source_user_id}`} className="text-purple-300 hover:text-purple-200 underline-offset-2 hover:underline" data-testid="report-source-user">
                      {report.source_user_name}
                    </Link>
                  </>
                )}
                {report.source_crew_id && report.source_crew_name && (
                  <>{report.source_user_name ? " · " : "About "}
                    <Link to={`/crews/${report.source_crew_id}`} className="text-emerald-300 hover:text-emerald-200 underline-offset-2 hover:underline" data-testid="report-source-crew">
                      {report.source_crew_name}
                    </Link>
                  </>
                )}
              </div>
            )}
            {report.description && (
              <div className="text-gray-200 text-base leading-relaxed whitespace-pre-wrap" data-testid="report-description">
                {report.description}
              </div>
            )}
          </div>
        </Card>
        <div className="mt-6 text-center">
          <Link to="/reports">
            <Button variant="outline" className="border-gray-700 text-gray-300">More RGM Reports →</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ReportDetailPage;
